import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, UserCheck } from 'lucide-react';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { feedbackPayArrived } from '../../utils/appFeedback';
import { useAutoCancelDraft } from '../../hooks/useAutoCancelDraft';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const formatEuro = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-GB', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
};

function Row({ label, value, valueColor, bold, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: big ? 15 : 14, color: 'var(--text-2)', fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span style={{ fontFamily: FONT, fontSize: big ? 22 : 16, fontWeight: bold ? 700 : 500, color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  );
}

export default function DemoAwaitingPage() {
  const navigate = useNavigate();
  const { txnId } = useParams();
  const [txn, setTxn] = useState(null);
  // Track whether the user handed off to the "Pay as guest (demo)" screen —
  // that screen owns the draft from then on, so we must NOT auto-cancel on
  // unmount in that case.
  const handedOffRef = useRef(false);

  // Auto-cancel the demo draft if the user navigates away / closes the
  // app without approving. Mirrors the real-transaction cleanup.
  useAutoCancelDraft({
    txnId,
    // Mask the status so the hook treats a handoff as "already resolved"
    // and doesn't fire a cancel.
    status: handedOffRef.current ? 'HANDED_OFF' : txn?.status,
    kind: 'demo',
  });

  useEffect(() => {
    if (!txnId) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchTxn = async () => {
      try {
        const res = await axios.get(`${API}/demo-transactions/${txnId}`, { headers });
        if (cancelled) return;
        setTxn(res.data);
        if (res.data.status === 'PAID') {
          feedbackPayArrived();
          navigate(`/demo-scan/complete/${txnId}`, { replace: true });
        } else if (res.data.status === 'CANCELLED' || res.data.status === 'EXPIRED') {
          // Silent recovery to the demo home — no error toast on timeout.
          navigate('/demo-scan', { replace: true });
        }
      } catch (_) {
        /* ignore */
      }
    };

    fetchTxn();
    const iv = setInterval(fetchTxn, 2000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [txnId, navigate]);

  const handleCancel = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/demo-transactions/${txnId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demo transaction cancelled');
      navigate('/demo-scan');
    } catch (_) {
      toast.error('Could not cancel');
    }
  }, [txnId, navigate]);

  return (
    <div className="app-shell" data-testid="demo-awaiting-page" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 60, paddingTop: 32, paddingLeft: 24, paddingRight: 24, position: 'relative' }}>
      <button
        type="button"
        onClick={() => navigate('/demo-scan')}
        aria-label="Close demo"
        data-testid="demo-awaiting-close"
        style={{ position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 18, width: 40, height: 40, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
      >
        <X size={20} color="var(--text)" />
      </button>

      <div style={{ marginTop: 20 }}>
        <RouteTransitionLogo width={110} height={98} dataTestId="demo-awaiting-logo" />
      </div>

      <div style={{ marginTop: 18, position: 'relative', width: 50, height: 50 }}>
        <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ animation: 'demo-spin 1.2s linear infinite' }}>
          <mask id="demo-await-mask" fill="white">
            <path d="M22.5 45C34.9264 45 45 34.9264 45 22.5C45 10.0736 34.9264 0 22.5 0C10.0736 0 0 10.0736 0 22.5C0 34.9264 10.0736 45 22.5 45Z"/>
          </mask>
          <path d="M53 22.5C47.6667 22.5 42.3333 22.5 37 22.5C37.0835 24.0383 36.9293 25.5723 36.5333 27.0617C35.0207 33.0736 29.1688 38.0114 22.5 38.25C14.1516 38.9132 5.72331 31.5833 5.5 22.5C4.82219 13.4845 12.7495 4.45873 22.5 4.25C30.1914 3.78647 38.066 8.89688 40.8133 16.5484C41.5149 18.4378 41.9172 20.4481 42 22.5C44 22.5 46 22.5 48 22.5C48.0845 19.8324 47.7511 17.1344 46.9955 14.5409C44.0934 4.07336 33.81 -4.06723 22.5 -4.25C8.2807 -5.04155 -5.40501 7.54576 -5.5 22.5C-6.30614 37.3864 6.87861 51.6696 22.5 51.75C34.8326 52.2694 47.1386 43.9561 51.2755 31.8489C52.3367 28.8555 52.9182 25.6811 53 22.5Z" fill="#1F8E2A" mask="url(#demo-await-mask)"/>
        </svg>
      </div>

      <h2 data-testid="demo-awaiting-headline" style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 14, marginBottom: 0 }}>
        Awaiting demo payment…
      </h2>

      {txn && (
        <div data-testid="demo-awaiting-bill" style={{ marginTop: 24, width: '100%', maxWidth: 340, background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 18, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Row label="Bill" value={formatEuro(txn.original_amount)} />
          <Row label={`Allure discount · ${Math.round(txn.discount_percent)}%`} value={`− ${formatEuro(txn.original_amount - txn.discounted_amount)}`} valueColor="var(--status-success-text)" />
          <div style={{ height: 1, background: 'var(--hairline)', margin: '4px 0' }} />
          <Row label="Customer pays" value={formatEuro(txn.discounted_amount)} bold big />
          {txn.customer_id && (
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6, marginBottom: 0, textAlign: 'right' }}>
              Customer ID · <span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{txn.customer_id}</span>
            </p>
          )}
        </div>
      )}

      {txn && txn.status === 'PENDING_GUEST' && (
        <div style={{ marginTop: 20, width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            data-testid="demo-awaiting-pay-as-guest-btn"
            onClick={() => { handedOffRef.current = true; navigate(`/demo-scan/pay/${txnId}`); }}
            style={{ width: '100%', height: 50, background: 'var(--cta-primary-bg)', color: 'var(--cta-primary-text)', border: 'none', borderRadius: 999, fontFamily: FONT, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: 'var(--premium-shadow-main)' }}
          >
            <UserCheck size={17} /> Pay as guest (demo)
          </button>
          <button
            type="button"
            data-testid="demo-awaiting-cancel-btn"
            onClick={handleCancel}
            style={{ width: '100%', height: 44, background: 'transparent', color: 'var(--status-danger-text)', border: '1px solid var(--status-danger-surface)', borderRadius: 999, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
          >
            Cancel transaction
          </button>
        </div>
      )}

      <p style={{ marginTop: 'auto', paddingTop: 24, fontFamily: FONT, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
        Demo mode · No real payment is processed.
      </p>

      <style>{`@keyframes demo-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
